/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import Select, {
	components,
	type ControlProps,
	type OptionsType,
	type OptionType,
} from '@atlaskit/select';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { performGqlFetch } from '@atlassian/conversation-assistant-utils/graphqlUtils';

const styles = cssMap({
	customControl: {
		width: '100%',
	},
});

export const JiraBulkTransitionsQuery = `
query GetStatuses($issueAris: [ID!]!) {
  jira {
    content: jiraFetchBulkOperationDetails(issueIds: $issueAris) @optIn(to: "JiraFetchBulkOperationDetailsResponse") {
      bulkTransitions(first: 100) {
        edges {
          node {
            transitions {
              edges {
                node {
                  id
                  transitionId
                  name
                  hasPreConditions
                  to {
                    id
                    name
                    statusId
                    description
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}
`;

type BulkJiraTransitionsResponse = {
	jira: {
		content: {
			bulkTransitions: {
				edges:
					| {
							node: {
								transitions: {
									edges:
										| {
												node: {
													id: string;
													transitionId: number | null;
													name: string | null;
													hasPreConditions: boolean | null;
													to: {
														id: string;
														name: string | null;
														statusId: string;
														description: string | null;
													};
												} | null;
										  }[]
										| null;
								} | null;
							} | null;
					  }[]
					| null;
			} | null;
		};
	};
};

type TransitionSelectorProps = {
	issueAris: string[];
	setSelectedTransition: (transition: OptionType) => void;
	selectedTransition?: OptionType;
	isDisabled: boolean;
	defaultTransitionId: number;
};

export const TransitionSelector = ({
	issueAris,
	setSelectedTransition,
	selectedTransition,
	isDisabled,
	defaultTransitionId,
}: TransitionSelectorProps): JSX.Element => {
	const { sendAnalyticsEvent } = useAnalytics();

	const loadOptions = useCallback(async (): Promise<OptionsType> => {
		const response = await performGqlFetch<BulkJiraTransitionsResponse, { issueAris: string[] }>({
			gqlQuery: JiraBulkTransitionsQuery,
			variables: {
				issueAris,
			},
		});
		const transitions =
			response.data.jira.content.bulkTransitions?.edges
				?.flatMap(
					(edge) =>
						edge?.node?.transitions?.edges?.map(
							(transitionEdge) =>
								transitionEdge?.node && {
									label: transitionEdge.node.name || '',
									value: transitionEdge.node.transitionId || '',
								},
						) || [],
				)
				.filter((option): option is { label: string; value: number } => option !== null) || [];
		const selectedTransition = transitions.find(
			(transition) => transition.value === defaultTransitionId,
		);
		if (selectedTransition) {
			setSelectedTransition(selectedTransition);
		}
		return transitions;
	}, [defaultTransitionId, issueAris, setSelectedTransition]);

	return (
		<Select
			testId="jira-issue-create-transition-selector"
			isDisabled={isDisabled}
			spacing="compact"
			appearance="subtle"
			value={selectedTransition}
			defaultOptions
			loadOptions={loadOptions}
			onChange={(selectedOption) => {
				if (selectedOption) {
					setSelectedTransition(selectedOption);
				}
			}}
			onMenuOpen={() => {
				sendAnalyticsEvent({
					action: 'opened',
					actionSubject: 'dropdown',
					actionSubjectId: 'rovoJiraIssueTransitionStatusSelector',
				});
			}}
			components={{
				Control: CustomControl,
			}}
		/>
	);
};

const CustomControl = ({ children, ...props }: ControlProps<OptionType>) => {
	return (
		<components.Control {...props}>
			<Box paddingInline="space.100" xcss={styles.customControl}>
				<Inline alignBlock="center">{children}</Inline>
			</Box>
		</components.Control>
	);
};
